import { supabase } from '@/integrations/supabase/client'
import { parseSheetRoads } from '@/lib/roads/api'
import type { RoadRecord } from '@/lib/roads/types'

type RpcResult<T> = { data: T | null; error: { message: string } | null }

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>
  ) => Promise<RpcResult<T>>)(fn, args)
  if (error) throw new Error(error.message)
  if (data === null) throw new Error('No response from server')
  return data
}

export interface AdminResponse {
  ok: boolean
  token?: string
  error?: string
}

export interface FlagRecord {
  id: string
  road_id: string | null
  road_name: string | null
  reason: string
  status: string
  created_at: string
  flagger_email: string | null
}

export interface ResyncStats {
  roads: number
  skipped: number
  geometryCleared?: number
}

export interface GeometryBackup {
  backed_up_at: string
  road_count: number
}

export interface HiddenRoadRecord {
  id: string
  road_id: string
  road_name: string | null
  reason: string
  review_at: string
  created_at: string
  hidden_by_email: string | null
}

export async function adminListHiddenRoads(token: string): Promise<HiddenRoadRecord[]> {
  const res = await rpc<AdminResponse & { hidden?: HiddenRoadRecord[] }>('admin_list_hidden_roads', {
    p_token: token,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.hidden ?? []
}

export async function adminRestoreHiddenRoad(token: string, roadId: string): Promise<void> {
  const res = await rpc<AdminResponse>('admin_restore_hidden_road', {
    p_token: token,
    p_road_id: roadId,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export async function adminListGeometryBackups(token: string): Promise<GeometryBackup[]> {
  const res = await rpc<AdminResponse & { backups?: GeometryBackup[] }>(
    'admin_list_geometry_backups',
    { p_token: token },
  )
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.backups ?? []
}

export async function adminRestoreGeometryBackup(
  token: string,
  backedUpAt: string,
): Promise<number> {
  const res = await rpc<AdminResponse & { restored?: number }>('admin_restore_geometry_backup', {
    p_token: token,
    p_backed_up_at: backedUpAt,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.restored ?? 0
}

export async function adminDeleteGeometryBackup(
  token: string,
  backedUpAt: string,
): Promise<number> {
  const res = await rpc<AdminResponse & { deleted?: number }>('admin_delete_geometry_backup', {
    p_token: token,
    p_backed_up_at: backedUpAt,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.deleted ?? 0
}

const SHEET_CSV_URL =
  'https://docs.google.com/spreadsheets/d/1l154s8MsRK5YSW0-u7_WyMkQ5WUBhGPj5q9c0DQnFUk/gviz/tq?tqx=out:csv&gid=0'

export async function adminLogin(password: string): Promise<{ token: string }> {
  const res = await rpc<AdminResponse>('admin_login', { p_password: password })
  if (!res.ok || !res.token) throw new Error(res.error ?? 'Login failed')
  return { token: res.token }
}

export async function adminListFlags(token: string): Promise<FlagRecord[]> {
  const res = await rpc<AdminResponse & { flags?: FlagRecord[] }>('admin_list_flags', {
    p_token: token,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.flags ?? []
}

export async function adminResolveFlag(token: string, id: string, resolved: boolean): Promise<void> {
  const res = await rpc<AdminResponse>('admin_resolve_flag', {
    p_token: token,
    p_id: id,
    p_resolved: resolved,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export interface SuggestionRecord {
  id: string
  email: string | null
  google_maps_url: string
  notes: string | null
  status: string
  created_at: string
}

export async function adminListSuggestions(token: string): Promise<SuggestionRecord[]> {
  const res = await rpc<AdminResponse & { suggestions?: SuggestionRecord[] }>(
    'admin_list_suggestions',
    { p_token: token },
  )
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.suggestions ?? []
}

export async function adminResolveSuggestion(
  token: string,
  id: string,
  accepted: boolean
): Promise<void> {
  const res = await rpc<AdminResponse>('admin_resolve_suggestion', {
    p_token: token,
    p_id: id,
    p_accepted: accepted,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export async function adminDeleteFlag(token: string, id: string): Promise<void> {
  const res = await rpc<AdminResponse>('admin_delete_flag', { p_token: token, p_id: id })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export async function adminChangePassword(
  token: string,
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const res = await rpc<AdminResponse>('admin_change_password', {
    p_token: token,
    p_current_password: currentPassword,
    p_new_password: newPassword,
  })
  if (!res.ok) throw new Error(res.error ?? 'Password change failed')
}

export interface ModRecord {
  user_id: string
  username: string | null
  created_at: string
}

export async function adminListMods(token: string): Promise<ModRecord[]> {
  const res = await rpc<AdminResponse & { mods?: ModRecord[] }>('admin_list_mods', {
    p_token: token,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
  return res.mods ?? []
}

export async function adminAddMod(token: string, username: string): Promise<void> {
  const res = await rpc<AdminResponse>('admin_add_mod', {
    p_token: token,
    p_username: username,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export async function adminRemoveMod(token: string, userId: string): Promise<void> {
  const res = await rpc<AdminResponse>('admin_remove_mod', {
    p_token: token,
    p_user_id: userId,
  })
  if (!res.ok) throw new Error(res.error ?? 'Request failed')
}

export interface FullResyncProgress {
  phase: 'sheet' | 'snapshot' | 'clear' | 'routing'
  current?: number
  total?: number
}

// Force Resync = the ultimate rebuild: fresh sheet pull -> snapshot ->
// wipe ALL cached OSRM geometry -> re-route every road from scratch.
// Fresh generation re-runs the full routing hierarchy (alternatives,
// motorway-excluded retry, mileage guard) and its audits, so new flags are
// created for every road the fresh polylines disagree with.
export async function adminForceResync(
  token: string,
  onProgress?: (p: FullResyncProgress) => void
): Promise<ResyncStats> {
  onProgress?.({ phase: 'sheet' })
  const res = await fetch(SHEET_CSV_URL)
  if (!res.ok) throw new Error(`Google Sheet fetch failed (${res.status})`)
  const csv = await res.text()
  const parsed = parseSheetRoads(csv)
  if (parsed.roads.length === 0) throw new Error('No valid roads found in sheet')

  onProgress?.({ phase: 'snapshot' })
  const payload: Record<string, unknown> = {
    roads: parsed.roads,
    meta: {
      fetched_at: new Date().toISOString(),
      row_count: parsed.rowCount,
      valid_count: parsed.roads.length,
      skipped_count: parsed.skipped,
      stale: false,
      source: 'admin_resync',
    },
  }

  const save = await rpc<AdminResponse>('admin_save_snapshot', {
    p_token: token,
    p_payload: payload,
    p_row_count: parsed.rowCount,
  })
  if (!save.ok) throw new Error(save.error ?? 'Save failed')

  onProgress?.({ phase: 'clear' })
  const clear = await rpc<AdminResponse & { deleted?: number }>('admin_clear_geometry_cache', {
    p_token: token,
  })
  if (!clear.ok) throw new Error(clear.error ?? 'Cache clear failed')
  const deleted = clear.deleted ?? 0

  // Re-route EVERY road with a Google Maps link (LineString candidates) in
  // sequential chunks — OSRM calls are sequential server-side, so a single
  // giant request would blow the function's time limit.
  const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '') as string
  const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '') as string
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error('Supabase not configured')

  const candidates = parsed.roads.filter((r) => r.google_maps_url)
  const links = [...new Set(candidates.map((r) => r.google_maps_url!))]
  const CHUNK = 25
  let routed = 0
  let failed = 0
  for (let i = 0; i < links.length; i += CHUNK) {
    const chunk = links.slice(i, i + CHUNK)
    onProgress?.({ phase: 'routing', current: Math.min(i + CHUNK, links.length), total: links.length })
    const geoRes = await fetch(`${SUPABASE_URL}/functions/v1/road-geometry`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        links: chunk,
        roads: candidates
          .filter((r) => chunk.includes(r.google_maps_url!))
          .map((r) => ({
            id: r.id,
            name: r.name,
            distance_miles: r.distance_miles,
            google_maps_url: r.google_maps_url,
          })),
      }),
    })
    if (!geoRes.ok) throw new Error(`Geometry service failed (${geoRes.status})`)
    const json = (await geoRes.json()) as {
      geometry?: Record<string, { coordinates?: unknown[] }>
    }
    for (const l of chunk) {
      const coords = json.geometry?.[l]?.coordinates
      if (Array.isArray(coords) && coords.length > 0) routed++
      else failed++
    }
  }

  return { roads: routed, skipped: failed + parsed.skipped, geometryCleared: deleted }
}

export interface TravelTimesStats {
  updated: number
  failed: number
  missing: number
}

// Re-run OSRM for every road whose cached polyline has no travel time yet
// (geometry generated before durations were recorded). Roads already carrying
// a duration are left untouched.
export async function adminGetTravelTimes(roads: RoadRecord[]): Promise<TravelTimesStats> {
  const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '') as string
  const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '') as string
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error('Supabase not configured')

  // LineString roads only — point roads (single pin) have no route to time.
  const candidates = roads.filter((r) => r.google_maps_url && r.geometry?.type === 'LineString')
  if (candidates.length === 0) return { updated: 0, failed: 0, missing: 0 }

  const links = [...new Set(candidates.map((r) => r.google_maps_url!))]
  const metaByLink = new Map(
    candidates.map((r) => [r.google_maps_url!, {
      id: r.id,
      name: r.name,
      distance_miles: r.distance_miles,
      google_maps_url: r.google_maps_url,
    }]),
  )

  // OSRM calls run sequentially server-side (~1-2s per road), so a single
  // request for every road at once would blow the function's time limit.
  // Chunk the links and run the chunks one after another.
  const CHUNK = 25
  let updated = 0
  let failed = 0
  let missing = 0
  for (let i = 0; i < links.length; i += CHUNK) {
    const chunk = links.slice(i, i + CHUNK)
    const res = await fetch(`${SUPABASE_URL}/functions/v1/road-geometry`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        links: chunk,
        roads: chunk.map((l) => metaByLink.get(l)).filter(Boolean),
        forceDurations: true,
      }),
    })
    if (!res.ok) throw new Error(`Travel time service failed (${res.status})`)
    const json = (await res.json()) as {
      force_updated?: number
      force_failed?: number
      geometry?: Record<string, { duration_s?: number | null }>
    }
    updated += json.force_updated ?? 0
    failed += json.force_failed ?? 0
    missing += chunk.filter(
      (l) => typeof json.geometry?.[l]?.duration_s !== 'number',
    ).length
  }
  return { updated, failed, missing }
}

export type { RoadRecord }
